import { Directive, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, ValidatorFn, ValidationErrors, AbstractControl } from '@angular/forms';
import { Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap, takeUntil, catchError } from 'rxjs/operators';
import { CepPlacesService } from '@services/cep-places.service';
import { CepResponse } from '@interfaces/novo-endereco';

const CEP_LENGTH = 8;
const DEBOUNCE_CEP_MS = 600;

/**
 * Validador que rejeita valores compostos apenas por espaços em branco.
 * Funciona como Validators.required mas faz trim() antes de verificar.
 */
export const requiredNaoEspacos: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value = control.value;

  // Trata null, undefined ou string vazia/apenas espaços
  if (value === null || value === undefined) {
    return { required: true };
  }

  if (typeof value === 'string' && value.trim().length === 0) {
    return { required: true };
  }

  return null;
};

@Directive()
export abstract class EnderecoFormBase implements OnInit, OnDestroy {
  public formulario: UntypedFormGroup;
  /** Resposta da consulta de CEP: null = carregando, false = erro, objeto = sucesso */
  public consultaCep: CepResponse | boolean | null = undefined;
  public alertLoading = false;
  public alertSucesso = false;
  public alertError = false;
  public mensagemErro = '';

  protected readonly destroy$ = new Subject<void>();

  constructor(
    protected readonly formularioBuilder: UntypedFormBuilder,
    protected readonly cepPlacesService: CepPlacesService,
  ) {}

  ngOnInit(): void {
    this.constroiFormulario();
    this.assinarBuscaCep();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  constroiFormulario(): void {
    this.formulario = this.formularioBuilder.group({
      codigoCep: ['', [Validators.required, this.cepConsultadoValidator()]],
      descricaoLogradouro: ['', requiredNaoEspacos],
      codigoNumero: ['',  requiredNaoEspacos],
      codigoComplemento: [''],
      codigoBairro: [''],
      codigoCidade: ['', Validators.required],
      codigoEstado: ['', Validators.required],
      codigoTipoEndereco: ['entrega'],
      descricaoReferencia: [''],
      nomeRespon1: [''],
      telefoneRespon1: [''],
      ramalTelefonRespon1: [''],
      nomeRespon2: [''],
      telefoneRespon2: [''],
      ramalTelefonRespon2: [''],
      nomeRespon3: [''],
      telefoneRespon3: [''],
      ramalTelefonRespon3: [''],
    });
  }

  /**
   * Adiciona ou remove Validators.required de um controle do formulário,
   * mantendo os demais validators existentes.
   */
  private setRequired(controlName: string, required: boolean): void {
    const control = this.formulario.get(controlName);
    if (!control) return;

    if (required) {
      control.setValidators(requiredNaoEspacos);
    } else {
      control.clearValidators();
    }
    
    control.updateValueAndValidity();
  }

  /**
   * Escuta alterações no CEP e busca automaticamente na API Places.
   * Só dispara quando o CEP tem 8 dígitos e não é 00000000.
   */
  private assinarBuscaCep(): void {
    this.formulario.get('codigoCep')?.valueChanges.pipe(
      debounceTime(DEBOUNCE_CEP_MS),
      distinctUntilChanged(),
      filter((cep: string) => this.cepValido(cep)),
      switchMap((cep: string) => {
        this.consultaCep = null;
        this.atualizarValidacaoCep();
        this.mensagemErro = '';
        return this.cepPlacesService.consultar(cep).pipe(
          catchError(() => {
            this.consultaCep = false;
            this.mensagemErro = 'Erro na consulta. Verifique o CEP.';
            this.atualizarValidacaoCep();
            this.limparCamposCep();
            return of({ _erro: true } as any);
          })
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe((response: any) => {
      if (response === null) {
        // 404 tratado pelo service — CEP não encontrado
        this.consultaCep = false;
        this.mensagemErro = 'CEP não encontrado.';
        this.atualizarValidacaoCep();
        this.limparCamposCep();
        return;
      }

      if (response._erro) {
        // Erro de rede já tratado pelo catchError do switchMap
        return;
      }

      if (response && response.cityName) {
        this.consultaCep = response;
        this.atualizarValidacaoCep();
        this.preencherCep(response);
      } else {
        this.consultaCep = false;
        this.mensagemErro = 'CEP não encontrado.';
        this.atualizarValidacaoCep();
        this.limparCamposCep();
      }
    });
  }

  /**
   * Limpa os campos preenchidos automaticamente pela consulta de CEP
   * e reabilita todos para edição manual.
   */
  private limparCamposCep(): void {
    this.formulario.patchValue({
      codigoCidade: '',
      codigoEstado: '',
      descricaoLogradouro: '',
      codigoBairro: '',
    });
    this.formulario.get('codigoCidade')?.enable();
    this.formulario.get('codigoEstado')?.enable();
    this.formulario.get('descricaoLogradouro')?.enable();
    this.formulario.get('codigoBairro')?.enable();
    this.setRequired('codigoBairro', false);
  }

  /**
   * Preenche os campos do formulário com os dados da API Places
   * e ajusta o estado enable/disable conforme as regras:
   * - Cidade e estado: sempre preenchidos e desabilitados
   * - Logradouro e bairro: se API retornar → preenche e desabilita; senão → limpa e habilita
   */
  private preencherCep(dados: CepResponse): void {
    this.formulario.patchValue({
      codigoCidade: dados.cityName?.toUpperCase(),
      codigoEstado: dados.uf?.toUpperCase(),
      descricaoLogradouro: dados.streetName?.toUpperCase() || '',
      codigoBairro: dados.district?.toUpperCase() || '',
    });

    this.formulario.get('codigoCidade')?.disable();
    this.formulario.get('codigoEstado')?.disable();

    this.toggleControlState('descricaoLogradouro', !!dados.streetName);
    this.toggleControlState('codigoBairro', !!dados.district);
    // Se o CEP não tem bairro (ex.: CEP de município inteiro), obriga o usuário a digitá-lo
    this.setRequired('codigoBairro', !dados.district);
  }

  buildEnderecoPayload(): object {
    const tipo = this.formulario.value.codigoTipoEndereco === 'entrega' ? 2 : 1;
    const v = this.formulario.getRawValue();
    return {
      codigoBairro: v.codigoBairro,
      codigoCep: v.codigoCep,
      codigoCidade: v.codigoCidade,
      codigoComplemento: v.codigoComplemento,
      codigoEstado: v.codigoEstado,
      codigoNumero: v.codigoNumero,
      codigoTipoEndereco: tipo,
      descricaoLogradouro: v.descricaoLogradouro,
      descricaoReferencia: v.descricaoReferencia,
      nomeRespon1: v.nomeRespon1,
      nomeRespon2: v.nomeRespon2,
      nomeRespon3: v.nomeRespon3,
      ramalTelefonRespon1: v.ramalTelefonRespon1,
      ramalTelefonRespon2: v.ramalTelefonRespon2,
      ramalTelefonRespon3: v.ramalTelefonRespon3,
      telefoneRespon1: v.telefoneRespon1,
      telefoneRespon2: v.telefoneRespon2,
      telefoneRespon3: v.telefoneRespon3,
    };
  }

  fecharAlertError(evento: string): void {
    this.alertError = false;
  }

  private cepValido(cep: string): boolean {
    if (!cep) return false;
    const digitos = cep.replace(/\D/g, '');
    return digitos.length === CEP_LENGTH && digitos !== '00000000';
  }

  /**
   * Retorna os dados da consulta de CEP para o template.
   */
  get consultaCepLoading(): boolean {
    return this.consultaCep === null;
  }

  get consultaCepErro(): boolean {
    return this.consultaCep === false;
  }

  get consultaCepSucesso(): boolean {
    return this.consultaCep !== null && this.consultaCep !== undefined && this.consultaCep !== false;
  }

  /**
   * Validador que invalida o CEP quando a consulta à API Places
   * falhou ou está em andamento, impedindo salvar com CEP inválido.
   * Quando consultaCep é undefined (nunca consultado), o validator passa
   * para não bloquear o formulário vazio.
   */
  private cepConsultadoValidator(): ValidatorFn {
    return (): ValidationErrors | null => {
      if (this.consultaCep === undefined || this.consultaCepSucesso) {
        return null;
      }
      return { cepInvalido: true };
    };
  }

  /**
   * Reavalia a validação do campo CEP sempre que o estado da consulta muda.
   */
  private atualizarValidacaoCep(): void {
    this.formulario.get('codigoCep')?.updateValueAndValidity();
  }

  private toggleControlState(controlName: string, enableIfFalse: boolean): void {
    const control = this.formulario.get(controlName);
    if (enableIfFalse) {
      control?.disable();
    } else {
      control?.enable();
    }
  }
}
